import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { defineMessages, useIntl } from 'react-intl';
import prettyBytes from 'pretty-bytes';
import { Button } from 'primereact/button';
import useSubscriptionExpiredRestrictions from '../hooks/useSubscriptionExpiredRestrictions';

const MAX_FILE_SIZE = 2097152;

const messages = defineMessages({
  dropFiles: {
    id: 'fileselector.drag-and-drop-message',
    defaultMessage: 'Drag and drop some files here, or click to select files',
  },
  import: { id: 'actions.import', defaultMessage: 'Import' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
});

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

/**
 * Renders a Dropzone and allows the user to select a file.
 * Expects
 * - intl
 * - onUpload(files)
 * - onCancel
 * @param {*} props
 */
const FileImportDropzone = ({ onUpload, onCancel, onAcceptedFiles, maxSize }) => {
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();
  const { formatMessage, locale } = useIntl();

  const { acceptedFiles, getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDropAccepted: onAcceptedFiles,
    minSize: 0,
    maxSize,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragAccept, isDragReject],
  );

  const files = acceptedFiles.map((file) => (
    <li key={file.path}>
      {file.path}
      {' - '}
      {prettyBytes(file.size, { locale })}
    </li>
  ));

  return (
    <section className="container">
      <div {...getRootProps({ className: 'dropzone' })} style={style}>
        <input {...getInputProps()} />
        <p>{formatMessage(messages.dropFiles)}</p>
      </div>
      <aside>
        <ul>{files}</ul>
      </aside>
      <Button
        label={formatMessage(messages.import)}
        disabled={acceptedFiles.length === 0 || subscriptionIsExpired}
        onClick={() => onUpload(acceptedFiles)}
      />
      {onCancel && <Button label={formatMessage(messages.cancel)} className="p-button-secondary" onClick={onCancel} />}
    </section>
  );
};

FileImportDropzone.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onAcceptedFiles: PropTypes.func,
  maxSize: PropTypes.string,
};

FileImportDropzone.defaultProps = {
  onAcceptedFiles: null,
  onCancel: null,
  maxSize: MAX_FILE_SIZE,
};

export default FileImportDropzone;
